import { ErrorBoundary } from "@sentry/react";
import { Alert, Button, Code, Group, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

// Per-route boundary. The app already wraps the whole <Switch> in a Sentry
// ErrorBoundary, but that means a crash in any route blanks the entire main
// content area. Wrapping individual high-risk surfaces (PimProductDetails,
// MappingProfileDetail, PimProductList, SchemaEditor) keeps the blast radius
// to that one feature — the user can navigate elsewhere without reloading.
//
// Sentry's ErrorBoundary auto-reports the captured error to Sentry, so we
// don't need a separate logger here.
// eslint-disable-next-line react/prop-types
export function FeatureErrorBoundary({ featureName, children }) {
  const { t } = useTranslation("common");
  return (
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <Alert
          color="red"
          icon={<IconAlertTriangle size={20} />}
          title={t("error_fallback.feature.title", {
            featureName: featureName ?? t("error_fallback.feature.thisSection"),
          })}
          m="md"
        >
          <Stack gap="sm">
            <Text size="sm">{t("error_fallback.feature.body")}</Text>
            <Code block style={{ maxHeight: 200, overflow: "auto" }}>
              {error?.message ?? String(error)}
            </Code>
            <Group>
              <Button
                size="xs"
                leftSection={<IconRefresh size={14} />}
                onClick={resetError}
              >
                {t("error_fallback.feature.tryAgain")}
              </Button>
              <Button size="xs" variant="light" onClick={() => window.location.reload()}>
                {t("error_fallback.reload")}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
